import { supabase } from "@/integrations/supabase/client";
import type { FanoutLegRow } from "@/lib/deliver-legs-fanout";

/**
 * Manual "In Transit" fan-out.
 *
 * The displayed order status is rolled up from the legs, so flipping only the
 * parent row leaves the chip reading "Confirmed"/"Booked". When an order is
 * marked in transit by hand, the first open stop is the one that departed.
 *
 *  - some stop already in transit or delivered → nothing to do
 *  - otherwise the lowest-sequence stop that isn't canceled goes in transit
 *  - `actual_departure_time` is write-once
 */
export interface LegInTransitUpdate {
  id: string;
  leg_sequence: number;
  previousStatus: string | null;
  update: { driver_status: "in_transit"; actual_departure_time?: string };
}

export function selectLegInTransitUpdate(
  legs: FanoutLegRow[],
  nowIso: string,
): LegInTransitUpdate | null {
  if (legs.some((l) => l.driver_status === "in_transit" || l.driver_status === "delivered")) {
    return null;
  }
  const first = [...legs]
    .sort((a, b) => a.leg_sequence - b.leg_sequence)
    .find((l) => l.driver_status !== "canceled");
  if (!first) return null;
  const update: LegInTransitUpdate["update"] = { driver_status: "in_transit" };
  if (!first.actual_departure_time) update.actual_departure_time = nowIso;
  return {
    id: first.id,
    leg_sequence: first.leg_sequence,
    previousStatus: first.driver_status ?? null,
    update,
  };
}

/** IO wrapper. Best-effort, like the delivered fan-out. */
export async function fanOutInTransitToLegs(args: {
  orderId: string;
  tenantId?: string | null;
  changedBy: string;
}): Promise<LegInTransitUpdate | null> {
  try {
    const { data, error } = await supabase
      .from("legs")
      .select("id, leg_sequence, driver_status, actual_departure_time, actual_arrival_time")
      .eq("order_id", args.orderId)
      .order("leg_sequence", { ascending: true });
    if (error) throw error;
    const u = selectLegInTransitUpdate((data ?? []) as FanoutLegRow[], new Date().toISOString());
    if (!u) return null;
    const { error: upErr } = await supabase.from("legs").update(u.update).eq("id", u.id);
    if (upErr) throw upErr;
    await supabase.from("order_change_log").insert({
      order_id: args.orderId,
      changed_by: args.changedBy || "Unknown",
      ...(args.tenantId ? { tenant_id: args.tenantId } : {}),
      field_name: `leg_${u.leg_sequence}_driver_status`,
      old_value: u.previousStatus,
      new_value: "in_transit",
    } as never);
    return u;
  } catch (err) {
    console.warn("[in-transit fan-out] failed to propagate in_transit to legs:", err);
    return null;
  }
}
